<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Warrants</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Generated Warrants</h1>
                <p class="mb-0 small">View and manage warrants</p>
              </div>
              <router-link :to="{ name: 'ViewVoidedWarrants' }" class="btn btn-sm btn-primary"
                v-if="isVoidedWarrantPresent">
                <span class="material-symbols-outlined" style="font-size:1rem;vertical-align:middle;">block</span>
                View Voided Warrants
              </router-link>
            </div>

            <div class="form-body">
              <div v-if="errorMessage" class="alert alert-warning alert-dismissible fade show" role="alert">
                {{ errorMessage }}
                <button type="button" class="btn-close" aria-label="Close" @click.prevent="clearError()"></button>
              </div>
              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label small fw-semibold">MDA:</label>
                    <SearchableSelect v-model="filters.mda" :options="uniqueMDAs" placeholder="All" small clearable />
                  </div>

                  <div class="col-md-2">
                    <label class="form-label small fw-semibold">From:</label>
                    <input type="date" class="form-control form-control-sm" v-model="filters.fromDate"
                      :max="filters.toDate || undefined" />
                  </div>

                  <div class="col-md-2">
                    <label class="form-label small fw-semibold">To:</label>
                    <input type="date" class="form-control form-control-sm" v-model="filters.toDate"
                      :min="filters.fromDate || undefined" />
                  </div>

                  <!-- <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date:</label>
                    <select class="form-select form-select-sm" v-model="filters.date">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div> -->

                  <!-- <div class="col-md-3">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status">
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div> -->

                  <div class="col-md-2 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 500px;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search by warrant number, org code or amount released..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Warrant No</th>
                        <th>Issue Date</th>
                        <th>MDA</th>
                        <th>Org. Code</th>
                        <th>Description</th>
                        <th>Amount Released</th>
                        <th>PV AVAIL. Balance</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-if="loading">
                        <tr v-for="n in pageLength" :key="'skeleton-' + n" class="skeleton-row">
                          <td>
                            <div class="skeleton-bar skeleton-sm mx-auto"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar skeleton-lg"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar skeleton-sm mx-auto"></div>
                          </td>
                        </tr>
                      </template>

                      <template v-else>
                        <tr v-for="(warrant, index) in paginatedItems" :key="warrant.id"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td data-label="S/N" class="serial-no text-center">{{ (currentPage - 1) * pageLength + index +
                            1
                            }}</td>
                          <td data-label="Warrant No" class="primary-col fw-semibold">
                            <router-link :to="{ name: 'ViewWarrant', params: { warrantId: warrant.id } }"
                              class="view-link text-decoration-underline" @mouseenter="showTooltip($event, warrant)"
                              @mouseleave="hideTooltip">
                              {{ warrant.warrant_no }}
                            </router-link>
                          </td>
                          <td data-label="Issue Date" class="secondary-col">{{ formatDate(warrant.issue_date) }}</td>
                          <td data-label="MDA" class="secondary-col mda-col">{{ warrant.mda }}</td>
                          <td data-label="Org. Code" class="secondary-col">{{ warrant.org_code }}</td>
                          <td data-label="Description" class="secondary-col description-col" :title="warrant.details">{{
                            warrant.details }}</td>
                          <td data-label="Amount Released" class="secondary-col fw-semibold text-end">{{
                            formatCurrency(warrant.amount_released) }}</td>
                          <td data-label="Balance" class="secondary-col fw-semibold text-end">{{
                            formatCurrency(warrant.payment_voucher_balance) }}</td>
                          <td data-label="Action" class="text-center action">
                            <div class="text-center d-flex col">
                              <router-link :to="{ name: 'ViewWarrant', params: { warrantId: warrant.id } }"
                                class="view-link text-decoration-none">
                                View
                              </router-link>
                              <span v-if="hasBizClientAccess([1001]) && !warrant.voided_warrant"> &nbsp;|&nbsp; </span>
                              <!-- <router-link :to="{ name: 'ViewGeneratePVWithWarrant', params: { warrantId: warrant.id } }"
                                v-if="hasBizClientAccess([1001])" class="view-link text-decoration-none">
                                Generate PV
                              </router-link> -->
                              <router-link
                                :to="{ name: 'ViewGeneratePVWithWarrantTemp', params: { warrantId: warrant.id } }"
                                v-if="hasBizClientAccess([1001]) && !warrant.voided_warrant"
                                class="view-link text-decoration-none">
                                Generate PV
                              </router-link>
                            </div>
                          </td>
                        </tr>

                        <!-- Tooltip -->
                        <div v-if="tooltipVisible && hoveredNotification" class="custom-tooltip" :style="{
                          top: tooltipY + 'px',
                          left: tooltipX + 'px'
                        }">
                          <div v-if="isLoadingDetails">
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading details...
                          </div>
                          <div v-else>
                            <div class="tooltip-header">
                              <p><strong>Details</strong></p>
                            </div>
                            <!-- WARRANT -->
                            <div>
                              <p><strong>Description: </strong>{{ hoveredNotification.details || 'N/A' }}</p>
                              <p><strong>Nature of Expenditure: </strong>{{ hoveredNotification.natureOfExpenditure ||
                                'N/A' }}</p>
                              <p><strong>Approving Sector Head: </strong>{{ hoveredNotification.approvingSectorHead ||
                                'N/A' }}</p>
                              <p><strong>Approving Director of Budget: </strong>{{ hoveredNotification.approvingDoB ||
                                'N/A' }}</p>
                              <p><strong>Imported Appraisal: </strong>{{ hoveredNotification.importedAppraisal ||
                                'N/A' }}</p>
                              <p><strong>Special Approval: </strong>{{ hoveredNotification.specApprovalWarrantInd ||
                                'N/A' }}</p>
                            </div>
                          </div>
                        </div>

                        <tr v-if="paginatedItems.length === 0">
                          <td :colspan="9" class="text-center text-muted py-4">
                            <div v-if="tableNotice">{{ tableNotice }}</div>
                            <div v-else>No warrants to display.</div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, totalElements) }} of
                  {{ totalElements }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0" :class="{ 'pe-none opacity-50': loading }">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <button class="page-link" @click.prevent="changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import SearchableSelect from '../common/SearchableSelect.vue'
import { debounce } from 'lodash'
// import jsPDF from 'jspdf'
// import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const warrants = ref([])
const voided_warrants = ref([])
const mdas = ref([])
const loading = ref(true)
const isLoadingDetails = ref(true)
const errorMessage = ref('')
const tableNotice = ref('')
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const hoveredNotification = ref(null)
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)

const showTooltip = async (event, warrant) => {
  hoveredNotification.value = warrant
  tooltipVisible.value = true

  const rect = event.currentTarget.getBoundingClientRect()

  tooltipX.value = rect.left + window.scrollX
  tooltipY.value = rect.bottom + window.scrollY + 8 // small offset

  await fetchWarrant(warrant.id)
}

const hideTooltip = () => {
  tooltipVisible.value = false
}

const fetchWarrant = async (id) => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/warrant/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    // Nature of Expenditure mapping
    if (data.natureOfExpenditure == 2) {
      data.natureOfExpenditure = 'Recurrent';
    } else if (data.natureOfExpenditure == 3) {
      data.natureOfExpenditure = 'Capital';
    } else if (data.natureOfExpenditure == 4) {
      data.natureOfExpenditure = 'Hybrid';
    }
    // Imported Appraisal mapping
    if (data.importedAppraisal == 1) {
      data.importedAppraisal = 'Yes';
    } else if (data.importedAppraisal == 0) {
      data.importedAppraisal = 'No';
    }
    // Special Approval mapping
    if (data.specApprovalWarrantInd == 1) {
      data.specApprovalWarrantInd = 'Yes';
    } else if (data.specApprovalWarrantInd == 0) {
      data.specApprovalWarrantInd = 'No';
    }
    hoveredNotification.value = {
      ...hoveredNotification.value,
      ...data
    }
  } catch (err) {
    console.error('Error fetching Warrant:', err)
  } finally {
    isLoadingDetails.value = false
  }
}

// Server-side pagination: `warrants` only ever holds the currently
// cached batch, not the full dataset. `batchStartIndex` is the global,
// 0-indexed offset of the first item in that batch.
const batchStartIndex = ref(0)
const totalElements = ref(0)

// Always fetch the backend's max page size (100), regardless of the UI's
// `pageLength`, so smaller page sizes (10/25/50) stay cached across more
// pages instead of triggering a network call on every page turn.
const getFetchSize = () => 100

// Which backend search param the current search term actually matched
// against (see resolveSearch below), and the value sent for it.
const activeSearchField = ref(null)
const activeSearchValue = ref(null)

import { useUserStore } from '@/stores/user'

// const router = useRouter()


const userStore = useUserStore()

// reactive computed wrapper around bizClientId
const bizClientId = computed(() => userStore.bizClientId || 1000)

// Check if user has business client access
const hasBizClientAccess = (requiredBizClients) => {
  if (!requiredBizClients || requiredBizClients.length === 0) return true
  return requiredBizClients.includes(bizClientId.value)

}

// fromDate/toDate are sent to the backend as-is: <input type="date"> already
// yields ISO yyyy-MM-dd, which matches the endpoint's LocalDate params.
// Empty means no bound on that side.
const defaultFilters = () => ({
  mda: '',
  date: '',
  fromDate: '',
  toDate: '',
  // status: ''
})

const filters = ref(defaultFilters())

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

// Drops the cached batch and re-fetches page 1 — used whenever something
// that changes what the backend returns (page size, mdaId, warrantNum)
// changes, since the old cache no longer applies.
const resetAndRefetch = async () => {
  currentPage.value = 1
  warrants.value = []
  batchStartIndex.value = 0
  await ensureBatchForPage(1)
}

watch(pageLength, resetAndRefetch)

// mdaId is filtered server-side, so a change requires a fresh fetch. `date`
// has no backend support and stays a client-side filter applied only to the
// currently loaded batch. `search` is handled separately by resolveSearch,
// since which backend field it maps to isn't known up front.
watch(() => filters.value.mda, resetAndRefetch)
watch(() => [filters.value.fromDate, filters.value.toDate], resetAndRefetch)
watch(() => filters.value.date, () => {
  currentPage.value = 1
})

// Fetches one batch of `size` warrants starting at backend page `backendPage`
// (0-indexed) via the paginated endpoint, and caches it in `warrants`.
// Pass `silent: true` when calling repeatedly as part of a probe (see
// resolveSearch) so it doesn't toggle the loading spinner or error/notice
// state on every intermediate attempt. Returns whether any warrants matched.
const fetchWarrantBatch = async (backendPage, size, { silent = false } = {}) => {
  const token = Cookies.get('auth_token')
  if (!silent) {
    errorMessage.value = ''
    loading.value = true
  }
  const isGroupScopedUser = !!(userStore.profile?.role?.groupHead || userStore.profile?.role?.groupMember)
  const groupTypeId = userStore.profile?.groupTypeId

  if (isGroupScopedUser && !groupTypeId) {
    if (!silent) {
      loading.value = false
      tableNotice.value = ''
    }
    return false
  }

  try {
    const endpoint = isGroupScopedUser
      ? `${root_api_url}/appraisal/warrant/all/${groupTypeId}`
      : `${root_api_url}/appraisal/warrant/all`

    const params = { page: backendPage, size }
    if (filters.value.mda) params.mdaId = filters.value.mda
    if (filters.value.fromDate) params.fromDate = filters.value.fromDate
    if (filters.value.toDate) params.toDate = filters.value.toDate
    if (activeSearchField.value) params[activeSearchField.value] = activeSearchValue.value

    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params,
    })

    warrants.value = response.data.content.map(warrant => ({
      id: warrant.id,
      appraisal_id: warrant.appraisalId,
      warrant_no: warrant.warrantNo || warrant.warrantNumber,
      issue_date: warrant.issueDate,
      mda: warrant.mda || warrant.mdaName,
      org_code: warrant.orgCode,
      amount_released: warrant.amountReleased,
      payment_voucher_balance: warrant.payVoucherBalance,
      status: warrant.status ?? 'active',
      nature_of_expenditure: warrant.natureOfExpenditure || 'N/A',
      details: warrant.details || '',
      voided_warrant: !!warrant.voidedWarrant,
    }))
    batchStartIndex.value = backendPage * size
    totalElements.value = response.data.page.totalElements

    if (!silent) {
      tableNotice.value = warrants.value.length === 0 ? 'No warrants found.' : ''
    }
    return warrants.value.length > 0
  } catch (error) {
    if (error.response && error.response.status === 404) {
      warrants.value = []
      totalElements.value = 0
      if (!silent) tableNotice.value = 'No warrants found.'
      return false
    }
    console.error('Failed to fetch warrants:', error)
    if (!silent) errorMessage.value = 'Failed to fetch warrants. Please try again.'
    return false
  } finally {
    if (!silent) loading.value = false
  }
}

// A single search box can't OR across warrantNum/orgCode/amtReleased in one
// backend call (the endpoint combines whichever params are passed, it
// doesn't match any-of), so probe each candidate field in turn and keep
// whichever one actually returns results.
const SEARCH_FIELDS = ['warrantNum', 'orgCode']

const resolveSearch = async () => {
  const term = debouncedSearch.value

  currentPage.value = 1
  warrants.value = []
  batchStartIndex.value = 0
  errorMessage.value = ''
  loading.value = true

  const fetchSize = getFetchSize()

  try {
    if (!term) {
      activeSearchField.value = null
      activeSearchValue.value = null
      await fetchWarrantBatch(0, fetchSize, { silent: true })
      tableNotice.value = warrants.value.length === 0 ? 'No warrants found.' : ''
      return
    }

    const attempts = SEARCH_FIELDS.map(field => ({ field, value: term }))
    // Strip currency/thousands formatting (e.g. "₦5,000" from formatCurrency)
    // before parsing, so amount searches work with values copied off the table.
    const cleanedTerm = term.replace(/[^0-9.]/g, '')
    const numericTerm = Number(cleanedTerm)
    if (cleanedTerm !== '' && !Number.isNaN(numericTerm)) {
      attempts.push({ field: 'amtReleased', value: numericTerm })
    }

    for (const attempt of attempts) {
      activeSearchField.value = attempt.field
      activeSearchValue.value = attempt.value
      // eslint-disable-next-line no-await-in-loop
      const found = await fetchWarrantBatch(0, fetchSize, { silent: true })
      if (found) {
        tableNotice.value = ''
        return
      }
    }

    // Nothing matched on any field.
    activeSearchField.value = null
    activeSearchValue.value = null
    warrants.value = []
    totalElements.value = 0
    tableNotice.value = 'No warrants found matching your search.'
  } catch (error) {
    console.error('Failed to fetch warrants:', error)
    errorMessage.value = 'Failed to fetch warrants. Please try again.'
  } finally {
    loading.value = false
  }
}

watch(debouncedSearch, resolveSearch)

// Makes sure the batch cached in `warrants` covers the requested UI page,
// fetching a fresh 50-item batch from the backend only when it doesn't.
const ensureBatchForPage = async (uiPage) => {
  const pageLen = pageLength.value
  const globalStart = (uiPage - 1) * pageLen
  const globalEnd = globalStart + pageLen - 1
  const cacheEnd = batchStartIndex.value + warrants.value.length - 1

  const isCached = warrants.value.length > 0 &&
    globalStart >= batchStartIndex.value &&
    globalEnd <= cacheEnd

  if (isCached) return

  const fetchSize = getFetchSize()
  const backendPage = Math.floor(globalStart / fetchSize)
  await fetchWarrantBatch(backendPage, fetchSize)
}

const isVoidedWarrantPresent = ref(false)

const fetchVoidedWarrantsCount = async () => {
  const token = Cookies.get('auth_token')

  const isGroupScopedUser = !!(userStore.profile?.role?.groupHead || userStore.profile?.role?.groupMember)
  const groupTypeId = userStore.profile?.groupTypeId

  if (isGroupScopedUser && !groupTypeId) {
    return
  }

  try {
    const endpoint = isGroupScopedUser
      ? `${root_api_url}/appraisal/warrant/voided/all/${groupTypeId}`
      : `${root_api_url}/appraisal/warrant/voided/all/`

    await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    isVoidedWarrantPresent.value = voided_warrants.value.length > 0

  } catch (error) {
    console.error('Failed to fetch voided warrant count:', error)
    isVoidedWarrantPresent.value = false
  }
}

const fetchMDAs = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    mdas.value = response.data.map(mda => ({
      id: mda.id,
      name: mda.name,
      organisation_code: mda.orgCode,
    }))
  } catch (error) {
    console.error('Failed to fetch MDAs:', error)
  }
}

const uniqueMDAs = computed(() => {
  return [...mdas.value].sort((a, b) => a.name.localeCompare(b.name))
})

const filteredItems = computed(() => {
  // Search (warrantNum) and MDA (mdaId) are filtered server-side by the
  // paginated endpoint, so `warrants.value` already reflects those. Only `date` has no
  // backend support and still needs to be applied client-side here, scoped
  // to whatever batch is currently loaded.
  let filtered = warrants.value

  // Apply date filter
  if (filters.value.date) {
    const now = new Date()
    filtered = filtered.filter(w => {
      const issueDate = new Date(w.issue_date)
      switch (filters.value.date) {
        case 'this_month':
          return issueDate.getMonth() === now.getMonth() &&
            issueDate.getFullYear() === now.getFullYear()
        case 'last_month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
          return issueDate.getMonth() === lastMonth.getMonth() &&
            issueDate.getFullYear() === lastMonth.getFullYear()
        }
        case 'this_year':
          return issueDate.getFullYear() === now.getFullYear()
        default:
          return true
      }
    })
  }

  // Apply date sorting (MOST RECENT FIRST)
  filtered = [...filtered].sort((a, b) => {
    return new Date(b.issue_date) - new Date(a.issue_date)
  })

  // Apply status filter
  // if (filters.value.status) {
  //   filtered = filtered.filter(w => w.status === filters.value.status)
  // }

  return filtered
})

const paginatedItems = computed(() => {
  // filteredItems is scoped to the currently cached batch, so slice using
  // the offset relative to that batch's start, not the global page index.
  const globalStart = (currentPage.value - 1) * pageLength.value
  const localStart = globalStart - batchStartIndex.value
  return filteredItems.value.slice(localStart, localStart + pageLength.value)
})

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(totalElements.value / pageLength.value))
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = async (page) => {
  if (page < 1 || page > totalPages.value) return
  await ensureBatchForPage(page)
  currentPage.value = page
}

const clearFilters = () => {
  filters.value = defaultFilters()
  search.value = ''
}

const clearError = () => {
  errorMessage.value = ''
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatCurrency = (amount) => {
  return '₦' + amount.toLocaleString('en-NG')
}

// const viewWarrant = (warrantId) => {
//   if (!warrantId) {
//     errorMessage.value = 'Unable to open warrant details: warrant ID is missing.'
//     return
//   }
//   router.push({ name: 'ViewWarrant', params: { warrantId } })
// }

const exportToPDF = () => {
  // Request the backend to generate/download a consolidated PDF for currently filtered warrants
  const token = Cookies.get('auth_token')
  const ids = filteredItems.value.map(w => w.id)
  errorMessage.value = ''

  axios.post(`${root_api_url}/appraisal/warrant/pdf`, { ids }, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'blob'
  }).then(response => {
    errorMessage.value = ''
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'warrants.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }).catch(err => {
    console.error('Failed to export PDF from backend:', err)
    if (err.response && err.response.status === 404) {
      errorMessage.value = 'No warrants available to export.'
    } else {
      errorMessage.value = 'Failed to export PDF. Please try again.'
    }
  })
}

// const downloadWarrantPDF = async (id) => {
//   const token = Cookies.get('auth_token')
//   try {
//     const response = await axios.post(`${root_api_url}/appraisal/warrant/pdf`, { ids: [id] }, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//       responseType: 'blob'
//     })

//     const blob = new Blob([response.data], { type: 'application/pdf' })
//     const url = window.URL.createObjectURL(blob)
//     const link = document.createElement('a')
//     link.href = url
//     link.download = `warrant-${id}.pdf`
//     document.body.appendChild(link)
//     link.click()
//     document.body.removeChild(link)
//     window.URL.revokeObjectURL(url)
//   } catch (error) {
//     console.error('Failed to download warrant PDF:', error)
//     if (error.response && error.response.status === 404) {
//       errorMessage.value = 'Requested warrant not found.'
//     } else {
//       errorMessage.value = 'Failed to download warrant PDF. Please try again.'
//     }
//   }
// }

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Warrants')

  // Add headers
  worksheet.addRow(['Warrant No', 'Issue Date', 'MDA', 'Org. Code', 'Amount Released'])

  // Add data
  filteredItems.value.forEach((warrant) => {
    worksheet.addRow([
      warrant.warrant_no,
      formatDate(warrant.issue_date),
      warrant.mda,
      warrant.org_code,
      formatCurrency(warrant.amount_released)
    ])
  })

  // Style header row
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  }

  // Auto-fit columns
  worksheet.columns.forEach(column => {
    column.width = 20
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'warrants.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  // MDAs don't depend on the user profile, so let that section load on its
  // own timeline instead of waiting behind the profile/warrants chain.
  fetchMDAs()

  try {
    if (!userStore.profile?.role || ((userStore.profile?.role?.groupHead || userStore.profile?.role?.groupMember) && !userStore.profile?.groupTypeId)) {
      await userStore.fetchProfile()
    }
  } catch {
    // profile fetch failed — don't block the page
  }

  // Warrants and voided-warrant count are independent of each other, so let
  // each display as soon as it resolves rather than one waiting on the other.
  ensureBatchForPage(1)
  fetchVoidedWarrantsCount()

  if (route.query.date) {
    filters.value.date = route.query.date
  }
  document.title = "View Warrants"
})

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color: var(--table-bg-head);
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.description-col {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mda-col {
  white-space: normal;
  word-break: break-word;
  min-width: 220px;
}

.view-link {
  color: #428bca;
  font-weight: 500;
}

.skeleton-row td {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}

.skeleton-bar {
  height: 14px;
  width: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #eceff1 25%, #f5f6f7 37%, #eceff1 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.skeleton-bar.skeleton-sm {
  width: 24px;
}

.skeleton-bar.skeleton-lg {
  width: 90%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}

.view-link:hover {
  color: #2a6496;
  text-decoration: underline !important;
}

.filters-section {
  background-color: var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--table-border);
}

.form-label {
  margin-bottom: 0.25rem;
  color: var(--color);
}

.showing-info {
  padding-left: 0.5rem;
}

.amount {
  padding-right: 3rem !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }

  .export-links {
    width: 100%;
    display: flex;
    gap: 1rem;
  }

  .export-link {
    flex: 1;
    text-align: center;
  }
}

.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background: #ffffff;
  color: #000;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 350px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  animation: fadeIn 0.15s ease-in-out;
}

.custom-tooltip p {
  margin: 0 0 6px;
}

.custom-tooltip p:last-child {
  margin-bottom: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
